import { ArmsPermissionClaimType, authService } from '../../services/Auth';
import { Button, Input } from '../../components';
import { Card, CardBody, CardTitle, Col, Form, FormGroup, InputGroup, InputGroupText, Row } from 'reactstrap';
import { Concentration, OutcomesType, ProfessionAddOnTrack } from '../../common/Types';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';

import MultiSelect from 'react-multi-select-component';
import Skeleton from 'react-loading-skeleton';
import { professionService } from '../../services/ProfessionAdministration';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const StyledMultiSelect = styled(MultiSelect)`
    width: 100%;
    max-width: 100%;
    margin-top: 1em;
    margin-bottom: 2em;
    padding-left: 1em;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }
    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        
        /* Remove borders from ALL nested elements */
        *, & > *, & * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
        }
        
        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
        
        /* Position checkboxes properly to the left */
        input, [role="checkbox"], .checkbox, span, div {
            margin-left: -20px !important;
            margin-right: 0.5rem !important;
        }
    }
    & .select-item {
        display: flex !important;
        align-items: center !important;
        padding-left: 0.125rem !important;
        padding-right: 0.5rem !important;
        
        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.25rem !important;
        }
        
        /* Override the negative margins for selected items to fill width */
        &.selected input, &.selected [role="checkbox"], &.selected .checkbox, 
        &.selected span, &.selected div {
            margin-left: 0 !important;
        }
    }
`;

const Exams = (props) => {
    const [exams, setExams] = useState<OutcomesType[]>();
    const [editing, setEditing] = useState(false);
    const [addOnTracks, setAddOnTracks] = useState<ProfessionAddOnTrack[]>();
    const [concentrations, setConcentrations] = useState<Concentration[]>();
    const { control, handleSubmit, reset, setValue, getValues, watch } = useForm();
    const { fields, append } = useFieldArray({
        control,
        name: 'exams',
    });

    const toggleEdit = () => {
        setEditing(!editing);
        reset({ exams: exams });
    };

    const remove = (index) => {
        const currentExam = getValues(`exams[${index}]`);
        (setValue as any)(`exams[${index}]`, { ...currentExam, isActive: false });
        reset(getValues());
    };

    const onSubmit = (formData) => {
        const toastId = toast.info('Saving information...');
        professionService
            .saveExams(formData.exams.map((exp) => new OutcomesType(exp)))
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Information Saved.',
                });
                toggleEdit();
                setExams(response);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save information.',
                });
            });
    };

    useEffect(() => {
        const getExams = () => {
            professionService
                .getExams(props.coa.professionId)
                .then((response) => {
                    append(response);
                    setExams(response);
                })
                .catch(() => {
                    toast.error('Unable to get information.');
                });
        };

        const getAddOnTracks = () => {
            professionService
                .getAddOnTracksByProfessionId(props.coa.professionId)
                .then((results) => {
                    setAddOnTracks(results);
                })
                .catch(() => {
                    toast.error('Error getting add-on track detail');
                });
        };

        const getConcentrations = () => {
            professionService
                .getProfessionConcentrations([props.coa.professionId])
                .then((results) => {
                    setConcentrations(results);
                })
                .catch(() => {
                    toast.error('Error getting concentrations.');
                });
        };

        getExams();
        getAddOnTracks();
        getConcentrations();
    }, [append, props.coa, setAddOnTracks, setExams, setConcentrations]);

    const changeConcentrationIds = (values: any, index: number) => {
        setValue(
            `exams[${index}].concentrationIds`,
            values.map((v) => v.value),
        );
    };

    const watchExams = watch('exams');

    return (
        <>
            <Col xs={12} md={6}>
                <Card style={{ display: 'inline-block' }}>
                    <CardBody>
                        <CardTitle>
                            <h3>Credentialing Exams</h3>
                        </CardTitle>
                        {editing ? (
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <FormGroup>
                                    {fields.map((exam, i) =>
                                        JSON.parse((exam as any).isActive) ? (
                                            <div key={i}>
                                                <div
                                                    className={`${
                                                        (concentrations?.length || 0) === 0
                                                            ? `d-flex justify-content-between`
                                                            : ``
                                                    }`}
                                                >
                                                    {addOnTracks && addOnTracks.length > 0 && (
                                                        <Controller
                                                            name={`exams[${i}].addOnTrackId`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type="select"
                                                                    {...field}
                                                                    value={
                                                                        field.value ?? (exam as any).addOnTrackId ?? ''
                                                                    }
                                                                    className={'mr-2'}
                                                                >
                                                                    <option value={''}>
                                                                        Add-On Track, if applicable
                                                                    </option>
                                                                    {addOnTracks &&
                                                                        addOnTracks.map((track, i) => (
                                                                            <option key={i} value={track.addOnTrackId}>
                                                                                {track.title}
                                                                            </option>
                                                                        ))}
                                                                </Input>
                                                            )}
                                                        />
                                                    )}
                                                    <InputGroup className="mb-1" style={{ minWidth: 350 }}>
                                                        <Controller
                                                            name={`exams[${i}].title`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    {...field}
                                                                    value={field.value ?? (exam as any).title ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        {
                                                            <InputGroupText>
                                                                <Button
                                                                    className="remove"
                                                                    color={'info'}
                                                                    onClick={() => remove(i)}
                                                                >
                                                                    <i className="remixicon-subtract-line" />
                                                                </Button>
                                                            </InputGroupText>
                                                        }
                                                    </InputGroup>
                                                    {concentrations && concentrations.length > 0 && (
                                                        <>
                                                            <StyledMultiSelect
                                                                options={(concentrations || [])
                                                                    .filter((c) => !c.isDeleted)
                                                                    .map((c) => {
                                                                        return {
                                                                            label: String(c.title),
                                                                            value: String(c.concentrationId),
                                                                        };
                                                                    })}
                                                                disableSearch={true}
                                                                hasSelectAll={false}
                                                                value={concentrations
                                                                    .filter(
                                                                        (c) =>
                                                                            watchExams[i]?.concentrationIds &&
                                                                            JSON.parse(
                                                                                `[${watchExams[i].concentrationIds}]`,
                                                                            ).filter((e) => e === c.concentrationId)
                                                                                .length > 0,
                                                                    )
                                                                    .map((c) => {
                                                                        return {
                                                                            label: c.title,
                                                                            value: c.concentrationId,
                                                                        };
                                                                    })}
                                                                onChange={(e) => changeConcentrationIds(e, i)}
                                                                labelledBy={'Concentration, if applicable'}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                                {addOnTracks?.length === 0 && (
                                                    <Controller
                                                        name={`exams[${i}].addOnTrackId`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <input
                                                                type={'hidden'}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                            />
                                                        )}
                                                    />
                                                )}
                                                <Controller
                                                    name={`exams[${i}].concentrationIds`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type={'hidden'}
                                                            {...field}
                                                            value={field.value ?? watchExams[i]?.concentrationIds ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].professionId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).professionId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].activeYear`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).activeYear ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).isActive ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].customId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).customId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].outcomeTypeId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).outcomeTypeId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].threshold`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).threshold ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ) : (
                                            <div key={i}>
                                                <Controller
                                                    name={`exams[${i}].title`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).title ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].addOnTrackId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type={'hidden'}
                                                            {...field}
                                                            value={field.value ?? (exam as any).addOnTrackId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].professionId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).professionId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].activeYear`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).activeYear ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).isActive ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].customId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).customId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].outcomeTypeId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).outcomeTypeId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`exams[${i}].threshold`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (exam as any).threshold ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ),
                                    )}
                                    <Button
                                        color={'info'}
                                        className="add"
                                        onClick={() =>
                                            append({
                                                professionId: props.coa.professionId,
                                                customId: 0,
                                                title: '',
                                                threshold: null,
                                                outcomeTypeId: null,
                                                isActive: true,
                                            })
                                        }
                                    >
                                        <i className="remixicon-add-line" />
                                        <span className="ml-1">Add Exam</span>
                                    </Button>
                                </FormGroup>
                                <Row className="mt-3">
                                    <Col>
                                        <Button type="submit">Save</Button>&nbsp;
                                        <Button outline onClick={() => toggleEdit()}>
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        ) : (
                            <>
                                {exams ? (
                                    <>
                                        {exams.map((exam, i) => {
                                            return (exam as any).isActive ? (
                                                <Row key={i}>
                                                    <Col className="exam-title">{(exam as any).title}</Col>
                                                </Row>
                                            ) : null;
                                        })}
                                        {authService.hasPermission(ArmsPermissionClaimType, 'adminexams') && (
                                            <Row className="mt-3">
                                                <Col>
                                                    <Button onClick={() => toggleEdit()}>Edit</Button>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                ) : (
                                    <Skeleton count={5} />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Col>
        </>
    );
};

export default Exams;
